import {motion, useAnimation} from 'framer-motion'
import {useEffect, useState} from 'react'
import {useIntersectionObserver} from 'usehooks-ts'

import type {ComponentProps} from 'react'

export type EEK =  'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G'

const colorMap: {value: EEK; fill: string}[] = [
  {value: 'A', fill: '#4fa260'},
  {value: 'B', fill: '#71ba5a'},
  {value: 'C', fill: '#beda5e'},
  {value: 'D', fill: '#fcf169'},
  {value: 'E', fill: '#eeb753'},
  {value: 'F', fill: '#df6a3d'},
  {value: 'G', fill: '#d73d38'},
]

export interface EEKGraphProps extends ComponentProps<'svg'> {
  selectedEEK: EEK
}

// Changeable
const height = 30
const baseWidth = 50
const arrowWidth = 20
const pointerWidth = 40
const rowGap = 5
const textPadding = 10

// do NOT change - this is calculated
const viewBoxWidth = colorMap.length * 2 * arrowWidth
const viewBoxHeight = colorMap.length * height - rowGap

export const EEKGraph = ({children, selectedEEK, ...restProps}: EEKGraphProps) => {
  const controls = useAnimation()
  const {ref, isIntersecting} = useIntersectionObserver({threshold: 0.7})
  const [doNotAnimate, setDoNotAnimate] = useState(['D', 'E', 'F', 'G'].includes(selectedEEK))

  useEffect(() => {
    if (!doNotAnimate) {
      if (isIntersecting) {
        controls.start('visible')
        setDoNotAnimate(true)
      } else {
        controls.start('hidden')
      }
    }
  }, [controls, isIntersecting, doNotAnimate])

  return (
    <svg
      viewBox={`0 ${rowGap * 2} ${viewBoxWidth} ${viewBoxHeight}`}
      {...restProps}
      ref={ref}
    >
      {colorMap.map(({value, fill}, idx) => {
        const yOffset = idx * height + rowGap
        const yCenter = yOffset + height / 2 + rowGap / 2
        const yEnd = yOffset + height
        const width = baseWidth + idx * arrowWidth

        return (
          <g key={idx}>
            <path
              d={`M0 ${yOffset + rowGap} H ${width} L ${
                width + arrowWidth
              },${yCenter} L ${width},${yEnd} L 0,${yEnd} Z`}
              style={{fill}}
            />
            <text
              y={yCenter}
              x={textPadding}
              style={{fill: 'white'}}
              dominantBaseline={'central'}
            >
              {value}
            </text>
            {value === selectedEEK && (
              <motion.g
                initial={doNotAnimate ? false : 'hidden'}
                animate={controls}
                variants={{
                  visible: {opacity: 1, x: 0, transition: {delay: 0.2, type: 'spring', duration: 2, stiffness: 150}},
                  hidden: {opacity: 0, x: 200},
                }}
              >
                <path
                  d={`M${viewBoxWidth} ${yOffset + rowGap} H ${viewBoxWidth - pointerWidth} L ${
                    viewBoxWidth - pointerWidth - arrowWidth
                  },${yCenter} L ${viewBoxWidth - pointerWidth},${yEnd} H ${viewBoxWidth} Z`}
                />
                <text
                  y={yCenter}
                  x={viewBoxWidth - textPadding}
                  style={{fill: 'white'}}
                  dominantBaseline={'central'}
                  textAnchor={'end'}
                >
                  {value}
                </text>
              </motion.g>
            )}
          </g>
        )
      })}
    </svg>
  )
}
